import { SchedulingDTO, ScheduleDTO } from '../../shared/DTO/SchedulingDTO';
import { ScheduleType, Constants } from '../../shared/constants';
import { MacroGroupsDTO, ParameterValueDTO, MacroGroupDTO, ParameterDTO } from '../../shared/DTO/MacroGroupsDTO';
import { Device } from '../devices/devices.model_new';
import { TranslateService } from '@ngx-translate/core';
import { RuleDefinitionDTO, RuleWrapperDTO, RuleDTO } from '../../shared/DTO/RuleDTO';
import { SCHEDULE_TYPE, RULE_SCHEDULE_TYPE, RULE_TYPE, MODE, COMMAND, RULE_STATUS } from './scheduling.constants';
import {
    EconomicTariffsDTO, PeriodDTO, EconomicPeriodDTO,
    CycleDTO, ThreePartTariffDTO, DualPartTariffDTO, EconomicTariffsDTOWrapper
} from '../../shared/DTO/EconomicTariffsDTO';
import { HOUSE_TARIFF, HOUSE_CYCLE } from '../houses/houses.constants';
import { SharedUtils } from '../../shared/shared.utils';
import { RuleWrapperPLCDTO, RuleDefinitionPLCDTO, RulePLCDTO } from '../../shared/DTO/RulePLCDTO';

export enum WEEKDAY {
    SUNDAY = 'Su',
    MONDAY = 'Mo',
    TUESDAY = 'Tu',
    WEDNESDAY = 'We',
    THURSDAY = 'Th',
    FRIDAY = 'Fr',
    SATURDAY = 'Sa'
}

export class WeekDay {
    constructor(private day: WEEKDAY, private set: boolean) { }

    getWeekDay(): WEEKDAY {
        return this.day;
    }

    getSet(): boolean {
        return this.set;
    }

    setSet(set: boolean) {
        this.set = set;
    }
}

export class Schedule {
    toDelete: boolean;
    fromTime;
    toTime;
    constructor(
        private type: SCHEDULE_TYPE,
        private id?: string,
        private daysOfWeek?: WeekDay[],
        fromTime?: string,
        toTime?: string,
        private temperature?: number,
        private moduleId?: string,
        private mode?: MODE,
        private power?: number,
        private command?: COMMAND,
        private state?: boolean,
        private ruleStatus?: RULE_STATUS
    ) {
        if (!daysOfWeek) {
            this.daysOfWeek = [];
            this.daysOfWeek[0] = new WeekDay(WEEKDAY.SUNDAY, false);
            this.daysOfWeek[1] = new WeekDay(WEEKDAY.MONDAY, false);
            this.daysOfWeek[2] = new WeekDay(WEEKDAY.TUESDAY, false);
            this.daysOfWeek[3] = new WeekDay(WEEKDAY.WEDNESDAY, false);
            this.daysOfWeek[4] = new WeekDay(WEEKDAY.THURSDAY, false);
            this.daysOfWeek[5] = new WeekDay(WEEKDAY.FRIDAY, false);
            this.daysOfWeek[6] = new WeekDay(WEEKDAY.SATURDAY, false);

        }

        if (!(fromTime && toTime)) {
            this.fromTime = '--:--';
            this.toTime = '--:--';
        } else {
            this.fromTime = fromTime;
            this.toTime = toTime;
        }

        if (!temperature && (type === SCHEDULE_TYPE.HVAC || type === SCHEDULE_TYPE.THERMOSTAT)) {
            this.temperature = 15;
        } else {
            this.temperature = temperature;
        }
    }

    static fromRuleDto(ruleDTO: RuleDTO): Schedule {
        let ruleState;
        let ruleStatus: RULE_STATUS;
        if (ruleDTO.rules.desired.rules) {
            ruleState = ruleDTO.rules.desired;
            ruleStatus = RULE_STATUS.DESIRED;
        } else if (ruleDTO.rules.reported.rules) {
            ruleState = ruleDTO.rules.reported;
            ruleStatus = RULE_STATUS.REPORTED;
        }
        if (!ruleState) {
            return undefined;
        }
        const ruleDefinitionDTO: RuleDefinitionDTO = ruleState.rules[0];
        const daysOfWeek: WeekDay[] = [];
        let state: boolean;
        let fromTime: string;
        let toTime: string;
        let temperature: number;
        let mode: MODE;
        let power: number;
        let command: COMMAND;
        for (let i = 0; i < Object.keys(WEEKDAY).length; i++) {
            daysOfWeek[i] = new WeekDay(WEEKDAY[Object.keys(WEEKDAY)[i]], ruleDefinitionDTO.activity.startRepeat.daysOfWeek.includes(i));
        }

        let schedulingType: SCHEDULE_TYPE;
        // console.log('rule type?', ruleState);
        if (ruleDTO.type === RULE_TYPE.LOW_COST_TARIFF) {
            schedulingType = SCHEDULE_TYPE.ECONOMIC;
        } else if (ruleDTO.type === RULE_TYPE.STAND_BY_KILLER) {
            schedulingType = SCHEDULE_TYPE.STAND_BY_KILLER;
        } else {
            switch (ruleState.rules[0].ruleType) {
                case RULE_SCHEDULE_TYPE.ON_OFF: {
                    schedulingType = SCHEDULE_TYPE.ON_OFF;
                    break;
                }
                case RULE_SCHEDULE_TYPE.THERMOSTAT: {
                    schedulingType = SCHEDULE_TYPE.THERMOSTAT;
                    break;
                }
                case RULE_SCHEDULE_TYPE.HVAC: {
                    schedulingType = SCHEDULE_TYPE.HVAC;
                    break;
                }
                default: {
                    schedulingType = SCHEDULE_TYPE.ECONOMIC;
                }
            }
        }

        if (schedulingType === SCHEDULE_TYPE.ON_OFF ||
            schedulingType === SCHEDULE_TYPE.HVAC ||
            schedulingType === SCHEDULE_TYPE.THERMOSTAT ||
            schedulingType === SCHEDULE_TYPE.STAND_BY_KILLER
        ) {
            fromTime = this.formatTime(ruleDefinitionDTO.activity.start.hour, ruleDefinitionDTO.activity.start.minute);
            toTime = ruleDefinitionDTO.activity.end ?
                this.formatTime(ruleDefinitionDTO.activity.end.hour, ruleDefinitionDTO.activity.end.minute) : '--:--';
        }

        if (schedulingType === SCHEDULE_TYPE.ON_OFF) {
            command = <COMMAND>ruleDefinitionDTO.activity.startAction.command;
        }

        if (schedulingType === SCHEDULE_TYPE.HVAC ||
            schedulingType === SCHEDULE_TYPE.THERMOSTAT) {
            temperature = ruleDefinitionDTO.activity.startAction.setpoint;
            mode = <MODE>ruleDefinitionDTO.activity.startAction.mode;
        }

        if (schedulingType === SCHEDULE_TYPE.STAND_BY_KILLER) {
            power = ruleDefinitionDTO.power;
        }

        state = ruleState.enabled;

        return new Schedule(schedulingType, ruleDTO.correlationId, daysOfWeek,
            fromTime, toTime, temperature, ruleDTO.moduleId, mode, power, command, state, ruleStatus);
    }

    static toRuleDefinitionDTO(schedule: Schedule): RuleDefinitionDTO {
        const ruleDefinitionDTO = new RuleDefinitionDTO();

        ruleDefinitionDTO.moduleId = schedule.getModuleId();

        let ruleScheduleType: RULE_SCHEDULE_TYPE;
        switch (schedule.getType()) {
            case SCHEDULE_TYPE.ON_OFF: {
                ruleScheduleType = RULE_SCHEDULE_TYPE.ON_OFF;
                break;
            }
            case SCHEDULE_TYPE.STAND_BY_KILLER: {
                ruleScheduleType = RULE_SCHEDULE_TYPE.STAND_BY_KILLER;
                break;
            }
            case SCHEDULE_TYPE.THERMOSTAT: {
                ruleScheduleType = RULE_SCHEDULE_TYPE.THERMOSTAT;
                break;
            }
            case SCHEDULE_TYPE.HVAC: {
                ruleScheduleType = RULE_SCHEDULE_TYPE.HVAC;
                break;
            }
        }
        ruleDefinitionDTO.type = ruleScheduleType;
        ruleDefinitionDTO.state = schedule.getState();
        ruleDefinitionDTO.activity = {
            start: {
                hour: undefined,
                minute: undefined
            },
            end: {
                hour: undefined,
                minute: undefined,
            },
            startRepeat: {
                daysOfWeek: []
            },
            startAction: {
                command: undefined,
                setpoint: undefined,
                mode: undefined
            }
        };

        for (let i = 0; i < schedule.getWeekDays().length; i++) {
            if (schedule.getWeekDays()[i].getSet()) {
                ruleDefinitionDTO.activity.startRepeat.daysOfWeek.push(i);
            }
        }

        if (schedule.getType() !== SCHEDULE_TYPE.ECONOMIC) {
            const startTime: number[] = this.unformatTime(schedule.getFromTime());
            ruleDefinitionDTO.activity.start.hour = startTime[0];
            ruleDefinitionDTO.activity.start.minute = startTime[1];

            if (schedule.getToTime()) {
                const endTime: number[] = this.unformatTime(schedule.getToTime());
                ruleDefinitionDTO.activity.end.hour = endTime[0];
                ruleDefinitionDTO.activity.end.minute = endTime[1];
            } else {
                ruleDefinitionDTO.activity.end = undefined;
            }
        } else {
            ruleDefinitionDTO.activity.start = undefined;
            ruleDefinitionDTO.activity.end = undefined;
        }

        if (schedule.getType() === SCHEDULE_TYPE.ON_OFF) {
            ruleDefinitionDTO.activity.startAction.command = schedule.getCommand() ? schedule.getCommand() : COMMAND.ON;
        }

        if (schedule.getType() === SCHEDULE_TYPE.STAND_BY_KILLER) {
            ruleDefinitionDTO.power = Number(schedule.getPower());
        }

        if (schedule.getType() === SCHEDULE_TYPE.THERMOSTAT ||
            schedule.getType() === SCHEDULE_TYPE.HVAC) {
            ruleDefinitionDTO.activity.startAction.setpoint = schedule.getTemperature();
        }

        if (schedule.getType() === SCHEDULE_TYPE.HVAC) {
            ruleDefinitionDTO.activity.startAction.mode = schedule.getMode();
        }

        if (schedule.getType() === SCHEDULE_TYPE.ECONOMIC) {
            ruleDefinitionDTO.activity.startAction = undefined;
        }
        return ruleDefinitionDTO;
    }

    static createScheduleFromPeriod(type: SCHEDULE_TYPE, id: string, weekdays: WeekDay[],
        weekday: WeekDay, economicPeriods: EconomicPeriod[]) {

        // console.log('weekdays', weekdays);
        const schedules: Schedule[] = [];
        weekdays.forEach(
            day => day.getWeekDay() === weekday.getWeekDay() ? true : day.setSet(false)
        );
        // console.log('weekdays', weekdays);
        economicPeriods.forEach(element => {
            schedules.push(new Schedule(type, id, weekdays,
                Schedule.formatTime(element.getBegin().getHour(), element.getBegin().getMinute()),
                Schedule.formatTime(element.getEnd().getHour(), element.getEnd().getMinute())));
        });
        return schedules;
    }

    private static unformatTime(time: string): number[] {
        const unformatedTime: number[] = [];
        unformatedTime[0] = this.unformatTimeUnit(time.split(':')[0]);
        unformatedTime[1] = this.unformatTimeUnit(time.split(':')[1]);
        return unformatedTime;
    }

    private static formatTime(hour: number, minute: number): string {
        return `${this.formatTimeUnit(hour)}:${this.formatTimeUnit(minute)}`;
    }

    private static formatTimeUnit(unit: number): string {
        const parsedUnit: string = unit.toString();
        return parsedUnit.length === 2 ? parsedUnit : `0${parsedUnit}`;
    }

    private static unformatTimeUnit(unformatedUnit: string): number {
        if (unformatedUnit[0] === '0') {
            return Number(unformatedUnit[1]);
        } else {
            return Number(unformatedUnit);
        }
    }

    static toDto(schedule: Schedule): ScheduleDTO {
        const scheduleDTO: ScheduleDTO = new ScheduleDTO();
        schedule.daysOfWeek.forEach(
            d => {
                switch (d.getWeekDay()) {
                    case WEEKDAY.MONDAY: {
                        scheduleDTO.Mon = d.getSet();
                        break;
                    }
                    case WEEKDAY.TUESDAY: {
                        scheduleDTO.Tue = d.getSet();
                        break;
                    }
                    case WEEKDAY.WEDNESDAY: {
                        scheduleDTO.Wed = d.getSet();
                        break;
                    }
                    case WEEKDAY.THURSDAY: {
                        scheduleDTO.Thu = d.getSet();
                        break;
                    }
                    case WEEKDAY.FRIDAY: {
                        scheduleDTO.Fri = d.getSet();
                        break;
                    }
                    case WEEKDAY.SATURDAY: {
                        scheduleDTO.Sat = d.getSet();
                        break;
                    }
                    case WEEKDAY.SUNDAY: {
                        scheduleDTO.Sun = d.getSet();
                        break;
                    }
                }
            }
        );

        return scheduleDTO;
    }

    private static unformatSelectedDaysMacroGroups(weekdays: WeekDay[]): string {
        const weekDaysToReturn = [];

        for (let i = 0; i < weekdays.length; i++) {
            if (weekdays[i].getSet()) {
                weekDaysToReturn.push(`"${i + 1}"`);
            }
        }

        return `[${weekDaysToReturn.toString()}]`;
    }

    private static addZeroBefore(n) {
        if (n === undefined) {
            n = '0';
        }
        return (Number(n) < 10 ? '0' : '') + n;
    }

    static weekDaysToNumbers(weekdays: WeekDay[]): number[] {
        return weekdays.map(wd => wd.getSet() ? (weekdays.indexOf(wd)) % 7 : Number.NaN);
    }

    getId(): string {
        return this.id;
    }

    setId(id: string): void {
        this.id = id;
    }

    setModuleId(moduleId: string): void {
        this.moduleId = moduleId;
    }

    getModuleId(): string {
        return this.moduleId;
    }

    getWeekDays(): WeekDay[] {
        return this.daysOfWeek;
    }

    getHorary(): string {
        return `${this.fromTime} to ${this.toTime}`;
    }

    getFromTime(): string {
        return this.fromTime;
    }

    setFromTime(fromTime: string): void {
        this.fromTime = fromTime;
    }

    getToTime(): string {
        return this.toTime;
    }

    setToTime(toTime: string): void {
        this.toTime = toTime;
    }

    getTemperature(): number {
        return this.temperature;
    }

    setTemperature(temperature: number) {
        this.temperature = temperature;
    }

    getMode(): MODE {
        return this.mode;
    }

    setMode(mode: MODE) {
        this.mode = mode;
    }

    getCommand(): COMMAND {
        return this.command;
    }

    setCommand(command: COMMAND) {
        this.command = command;
    }

    getPower(): number {
        return this.power;
    }

    setPower(power: number) {
        this.power = power;
    }

    getType(): SCHEDULE_TYPE {
        return this.type;
    }

    setType(type: SCHEDULE_TYPE): void {
        this.type = type;
    }

    getState(): boolean {
        return this.state;
    }

    setState(state: boolean) {
        this.state = state;
    }

    getRuleStatus(): RULE_STATUS {
        return this.ruleStatus;
    }

    setRuleStatus(ruleStatus: RULE_STATUS) {
        this.ruleStatus = ruleStatus;
    }
}

export class Scheduling {

    constructor(private schedules?: Schedule[]) {
        if (!schedules) {
            this.schedules = [];
        }
    }

    static fromRuleWrapperDTO(ruleWrapperDTO: RuleWrapperDTO): Scheduling {
        const schedules: Schedule[] = [];
        if (ruleWrapperDTO.Rules) {
            ruleWrapperDTO.Rules.forEach(rule => {
                if (Schedule.fromRuleDto(rule)) {
                    schedules.push(Schedule.fromRuleDto(rule));
                }
            }
            );
        }
        return new Scheduling(schedules);
    }

    getSchedules(module?: Device): Schedule[] {
        return this.schedules.filter(s => !module || (module && s.getModuleId() === module.getModuleId()));
    }

    hasSchedules(): boolean {
        return this.schedules.length > 1 ||
            (this.schedules.length === 1 && this.schedules[0].getWeekDays().some(wd => wd.getSet()));
    }

    addSchedule(schedule: Schedule): void {
        this.schedules.push(schedule);
    }

    removeSchedule(schedule: Schedule): void {
        this.schedules.forEach((s, index) => {
            if (s.getId() === schedule.getId()) {
                this.schedules.splice(index, 1);
            }
        });
    }

    updateSchedule(schedule: Schedule): void {
        this.schedules.forEach((s, index) => {
            if (s.getId() === schedule.getId()) {
                this.schedules[index] = schedule;
            }
        });
    }
}

export class PowerLimitControl {
    constructor(
        private id: string,
        private enabled: boolean,
        private powerLimit: number,
        private priorityDisconnect?: string[],
        private priorityReconnect?: string[]) { }

    static createDummyPLC() {
        return new PowerLimitControl('0',
            false,
            0,
            [],
            null);
    }

    static fromRuleWrapperDto(ruleWrapperPLCDTO: RuleWrapperPLCDTO): PowerLimitControl {
        const ruleDTO: RulePLCDTO = ruleWrapperPLCDTO['powerLimitControl'][0];
        // console.log(ruleDTO);
        let ruleState;
        if (ruleDTO.rules.desired.rules) {
            ruleState = ruleDTO.rules.desired;
        } else if (ruleDTO.rules.reported.rules) {
            ruleState = ruleDTO.rules.reported;
        }
        const ruleDefinitionDTO: RuleDefinitionPLCDTO = ruleState.rules[0];
        // console.log(ruleDefinitionDTO);
        // console.log(ruleDefinitionDTO.moduleIds.priorityDisconnect);
        // console.log(ruleDefinitionDTO.moduleIds.priorityReconnect);
        return new PowerLimitControl(ruleDTO.correlationId,
            ruleState.enabled,
            ruleDefinitionDTO.powerLimit,
            ruleDefinitionDTO.moduleIds.priorityDisconnect,
            ruleDefinitionDTO.moduleIds.priorityReconnect);
    }

    static toRuleDefinitionDto(powerLimitPower: PowerLimitControl): RuleDefinitionPLCDTO {
        const ruleDefinitionDTO: RuleDefinitionPLCDTO = new RuleDefinitionPLCDTO();
        ruleDefinitionDTO.state = powerLimitPower.enabled;
        ruleDefinitionDTO.powerLimit = powerLimitPower.powerLimit;
        ruleDefinitionDTO.moduleIds = {
            priorityDisconnect : powerLimitPower.priorityDisconnect,
            priorityReconnect : undefined
        };
        //console.log(ruleDefinitionDTO);
        return ruleDefinitionDTO;
    }

    getId(): string {
        return this.id;
    }

    getEnabled(): boolean {
        return this.enabled;
    }

    setEnabled(enabled: boolean): void {
        this.enabled = enabled;
    }

    getPowerLimit(): number {
        return this.powerLimit;
    }

    setPowerLimit(powerLimit: number): void {
        this.powerLimit = powerLimit;
    }

    getPriorityDisconnect(): string[] {
        return this.priorityDisconnect;
    }

    setPriorityDisconnect(priorityDisconnect: string[]) {
        this.priorityDisconnect = priorityDisconnect;
    }

    getPriorityReconnect(): string[] {
        return this.priorityReconnect;
    }

    setPriorityReconnect(priorityReconnect: string[]) {
        this.priorityReconnect = priorityReconnect;
    }

}


export class EconomicTariffs {
    constructor(private dualPartTariff: DualPartTariff, private threePartTariff: ThreePartTariff) { }

    static fromDTO(economicTariffsDTOWrapper: EconomicTariffsDTOWrapper) {
        return new EconomicTariffs(DualPartTariff.fromDTO(economicTariffsDTOWrapper.tariffs.dualPartTariff),
            ThreePartTariff.fromDTO(economicTariffsDTOWrapper.tariffs.threePartTariff));
    }

    getTariff(tariff: HOUSE_TARIFF) {
        if (tariff === HOUSE_TARIFF.BI) {
            return this.dualPartTariff;
        } else if (tariff === HOUSE_TARIFF.TRI) {
            return this.threePartTariff;
        }
    }

    getCycle(tariff: HOUSE_TARIFF, cycle: HOUSE_CYCLE): Cycle {
        if (tariff === HOUSE_TARIFF.BI) {
            return this.dualPartTariff.getCycle(cycle);
        } else if (tariff === HOUSE_TARIFF.TRI) {
            return this.threePartTariff.getCycle(cycle);
        }
    }

    getEconomicPeriod(tariff: HOUSE_TARIFF, cycle: HOUSE_CYCLE): EconomicPeriod[] {
        if (tariff === HOUSE_TARIFF.BI) {
            return this.dualPartTariff.getWeeklyCycleSummer().getWorkingDays();
        } else if (tariff === HOUSE_TARIFF.TRI) {
            return this.threePartTariff.getWeeklyCycleSummer().getWorkingDays();
        }
    }
}

class DualPartTariff {
    constructor(public supportedOptions: string[],
        private weeklyCycleSummer: Cycle,
        private weeklyCycleWinter: Cycle,
        private dailyCycle: Cycle) { }

    static fromDTO(dualPartTariffDTO: DualPartTariffDTO) {

        const weeklyCycleSummer = Cycle.fromDTO(dualPartTariffDTO.weeklyCycleSummer);
        const weeklyCycleWinter = Cycle.fromDTO(dualPartTariffDTO.weeklyCycleWinter);
        const dailyCycle = Cycle.fromDTO(dualPartTariffDTO.dailyCycle);
        return new DualPartTariff(dualPartTariffDTO.supportedOptions,
            weeklyCycleSummer,
            weeklyCycleWinter,
            dailyCycle);
    }

    getWeeklyCycleSummer() {
        return this.weeklyCycleSummer;
    }

    getWeeklyCycleWinter() {
        return this.weeklyCycleWinter;
    }

    getDailyCycle() {
        return this.dailyCycle;
    }

    getCycle(cycle: HOUSE_CYCLE): Cycle {
        if (cycle === HOUSE_CYCLE.DAILY) {
            return this.dailyCycle;
        } else if (cycle === HOUSE_CYCLE.WEEKLY) {
            return SharedUtils.isDaylightSavingsTime() ? this.weeklyCycleSummer : this.weeklyCycleWinter;
        }
    }


}

class ThreePartTariff {
    constructor(public supportedOptions: string[],
        private weeklyCycleSummer: Cycle,
        private weeklyCycleWinter: Cycle,
        private dailyCycleSummer: Cycle,
        private dailyCycleWinter: Cycle) { }

    static fromDTO(threePartTariffDTO: ThreePartTariffDTO) {
        const weeklyCycleSummer = Cycle.fromDTO(threePartTariffDTO.weeklyCycleSummer);
        const weeklyCycleWinter = Cycle.fromDTO(threePartTariffDTO.weeklyCycleWinter);
        const dailyCycleSummer = Cycle.fromDTO(threePartTariffDTO.dailyCycleSummer);
        const dailyCycleWinter = Cycle.fromDTO(threePartTariffDTO.dailyCycleWinter);
        return new ThreePartTariff(threePartTariffDTO.supportedOptions,
            weeklyCycleSummer,
            weeklyCycleWinter,
            dailyCycleSummer,
            dailyCycleWinter);
    }

    getWeeklyCycleSummer() {
        return this.weeklyCycleSummer;
    }

    getWeeklyCycleWinter() {
        return this.weeklyCycleWinter;
    }

    getDailyCycleSummer() {
        return this.dailyCycleSummer;
    }

    getDailyCycleWinter() {
        return this.dailyCycleWinter;
    }

    getCycle(cycle: HOUSE_CYCLE): Cycle {
        if (cycle === HOUSE_CYCLE.DAILY) {
            return SharedUtils.isDaylightSavingsTime() ? this.dailyCycleSummer : this.dailyCycleWinter;
        } else if (cycle === HOUSE_CYCLE.WEEKLY) {
            return SharedUtils.isDaylightSavingsTime() ? this.weeklyCycleSummer : this.weeklyCycleWinter;
        }
    }
}

export class Cycle {

    constructor(private workingDays: EconomicPeriod[], private saturday: EconomicPeriod[], private sunday: EconomicPeriod[]) { }
    static fromDTO(cycleDTO: CycleDTO) {

        let workingDays: EconomicPeriod[];
        let saturday: EconomicPeriod[];
        let sunday: EconomicPeriod[];

        if (cycleDTO.workingDays) {
            workingDays = cycleDTO.workingDays.economicPeriods.map(element =>
                EconomicPeriod.fromDTO(element)
            );
        }
        if (cycleDTO.saturday) {
            saturday = cycleDTO.saturday.economicPeriods.map(element =>
                EconomicPeriod.fromDTO(element)
            );
        }
        if (cycleDTO.sunday) {
            sunday = cycleDTO.sunday.economicPeriods.map(element =>
                EconomicPeriod.fromDTO(element)
            );
        }

        return new Cycle(workingDays, saturday, sunday);
    }

    getWorkingDays() {
        return this.workingDays;
    }

    getSaturday() {
        return this.saturday;
    }

    getSunday() {
        return this.sunday;
    }

    getEconomicPeriod(cycle: HOUSE_CYCLE, weekday: WEEKDAY) {
        if (cycle === HOUSE_CYCLE.DAILY) {
            return this.workingDays;
        } else if (cycle === HOUSE_CYCLE.WEEKLY) {
            switch (weekday) {
                case WEEKDAY.SUNDAY: {
                    return this.sunday;
                }
                case WEEKDAY.SATURDAY: {
                    return this.saturday;
                }
                default: {
                    return this.workingDays;

                }
            }

        }
    }
}

export class EconomicPeriod {

    constructor(private begin: Period, private end: Period) { }
    static fromDTO(economicPeriodDTO: EconomicPeriodDTO) {
        return new EconomicPeriod(Period.fromDTO(economicPeriodDTO.begin), Period.fromDTO(economicPeriodDTO.end));
    }

    getBegin() {
        return this.begin;
    }


    getEnd() {
        return this.end;
    }
}

class Period {
    constructor(private hour: number, private minute: number) { }
    static fromDTO(period: PeriodDTO) {
        return new Period(period.hour, period.minute);
    }
    getHour() {
        return this.hour === 24 ? 23 : this.hour;
    }
    getMinute() {
        return this.hour === 24 ? 59 : this.minute;
    }
}



// WEBPACK FOOTER //
// ./src/app/entities/scheduling/scheduling.model.ts